import { useState, useEffect, type ReactNode } from "react";
import { AuthContext } from "./AuthContext";
import type { User } from "@/types/auth";
import { setAccessToken } from "@/lib/axiosClient";
import { getCurrentUser, refreshSession } from "@/services/auth";

export const AuthProvider = ({ children }: { children: ReactNode }) => {

    // 1. STATE (Murni di Memori, Default Null)
    // ❌ HAPUS semua logic localStorage di sini
    const [user, setUserState] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper: Dipanggil saat Login Berhasil
    const setAuthSuccess = (newUser: User, token: string) => {
        setAccessToken(token); // Simpan token di variable axiosClient (RAM)
        setUserState(newUser); // Simpan user di state React
    };

    // Helper: Update User (bisa null untuk logout lokal)
    const setUser = (newUser: User | null) => {
        if (!newUser) {
            setAccessToken(null); // Bersihkan token memory
        }
        setUserState(newUser);
    };

    // 2. INITIALIZATION (Saat Aplikasi Dibuka / F5 Refresh)
    // Karena data ada di RAM (hilang saat refresh), kita tanya Server via Cookie
    useEffect(() => {
        const initAuth = async () => {
            try {
                // Minta Access Token baru (Browser otomatis kirim Cookie HttpOnly)
                const token = await refreshSession();

                if (token) {
                    // Jika Server kasih token, berarti user valid
                    setAccessToken(token);

                    // Ambil detail profil user
                    const userData = await getCurrentUser();

                    if (userData) {
                        setUserState(userData);
                    } else {
                        setUser(null);
                    }
                } else {
                    // Server tolak (Cookie expired/tidak ada)
                    setUser(null);
                }
            } catch (error) {
                console.error("Gagal restore session:", error);
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    // 3. SINKRONISASI ANTAR TAB (Fitur "Walkie-Talkie")
    // Mendengarkan jika Tab lain berteriak "LOGOUT_SIGNAL"
    useEffect(() => {
        const channel = new BroadcastChannel("auth_channel");

        channel.onmessage = (event) => {
            if (event.data === "LOGOUT_SIGNAL") {
                console.log("Tab sebelah logout. Tab ini ikut logout...");

                // 1. Hapus Token Memori
                setAccessToken(null);

                // 2. Hapus User State
                // (Otomatis memicu PrivateRoute untuk menendang ke Login)
                setUserState(null);
            }
        };

        return () => {
            channel.close();
        };
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                setAuthSuccess,
                isAuthenticated: !!user,
                loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};